import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EControlType } from 'components/ControlWrapper';
import { CancellationFieldsByCategory, cloneBenefit, createOpportunityOptionAndBenefits, FieldsToLookupByMasterAttributeId, friendlyValueToFlexibleFieldValue, isBaseAdminQuoteTypeCode } from 'global/Application';
import { emitEventForBenefitPropertyChange, emitEventForFieldChange } from 'global/EmitEvent/Delta';
import { addNMonthsToDateString, ArrayToLookupByPredicate, ArrayToLookupByPredicateWithMap, ArrayToMapByPredicateWithMap, DateToYYYYMMDDHHMMSS } from 'global/Utility/TSFunctions';
import { applyVisibility, ApplyVisibilityFields, applyVisibilityNonstandard, applyVisibilityNonstandardTemplate, BuildVisibilityScope } from 'global/Visibility/GlobalVisibility';
import { IFieldReference } from 'global/Visibility/IFieldReference';
import { ParentVisibilityGraphProcessor } from 'global/Visibility/ParentVisibilityGraph';
import { bcbs_config_masterattribute } from 'model/AttributeOptionSets.type';
import * as IXrmTypes from 'model/CRM';
import { Ibcbs_agencydetail_extended, ISystemUserExtended } from 'model/CRM.type';
import * as FAM from 'model/FlexibleAttributeModel';
import { IComment } from "model/FlexibleAttributeModel";
import { ICheckList } from 'model/ICheckList';
import { IGroupConfigurationUpdate } from 'model/IGroupConfigurationUpdate';
import { blankSnapshot } from 'model/snapshotTemplate';
import { IGroupStructureCESWizardItem } from 'views/BlueRelationship/GroupStructure/GroupStructureCESWizard/IGroupStructureCESWizardItem.type';
import { ITierChange } from 'views/TierWizard';

export interface IFlexibleAttributePayload {
  category?: FAM.IBenefit | IXrmTypes.bcbs_config_category;
  plan?: FAM.IPlan;
  scope: FAM.IPlan | FAM.IFeesAndPremiums | FAM.IBenefit | FAM.IGroupConfigurationConfig | FAM.INonStandard;
  field?: FAM.IFlexibleField
  fieldValue: FAM.IFlexibleFieldValue;
  nonStandardLevel?: string

  //TODO POC/TRIAL;this may help with performance/ 
  route?: any;
  value?: FAM.TValue;

  // Time to live for the change
  ttl?: number;
}

export interface IChangePropertyPayload {
  type: 'bcbs_opportunityoption',
  category?: FAM.IBenefit | IXrmTypes.bcbs_config_category;
  scope: FAM.IOpportunityProductOptionBenefit;
  property?: keyof FAM.IOpportunityProductOptionBenefit;
  value?: FAM.TValue;
}


export type TChangeTracker = Record<
  string,
  {
    field: FAM.IFlexibleField,
    fieldValue: FAM.IFlexibleFieldValue,
    value: FAM.TValue,
  }
>;


export interface IAttributeSetPayload {
  category: IXrmTypes.bcbs_config_category;
  scope: FAM.IPlan | FAM.IFeesAndPremiums | FAM.IBenefit | FAM.IGroupConfigurationData;
  config: FAM.IGroupConfigurationConfig;
  attributeSets: FAM.IFlexibleFieldSet[];
  changeTracker: TChangeTracker;
  delete?: boolean;
}

export interface INonStandardPayload {
  config: FAM.IGroupConfigurationConfig;
  benefit?: FAM.IBenefit;
  groupConfiguration?: FAM.IGroupConfigurationData;
  nonStandards: FAM.INonStandard[];
  changeTracker: TChangeTracker;
}

export interface INonStandardTemplatePayload {
  //config: FAM.IGroupConfigurationConfig;
  //benefit?: FAM.IBenefit;
  //groupConfiguration?: FAM.IGroupConfigurationData;
  //nonStandards: FAM.INonStandard[];
  //changeTracker: TChangeTracker;

  nonStandardTemplates: FAM.INonStandardTemplate[];
}

export interface IAddFeesPayload {
  stopLoss: FAM.IStopLoss,
  feesAndPremiums?: FAM.IFeesAndPremiums
}

export interface IChangeCESPayload {
  oldCES: FAM.ICESNumber;
  newCES: FAM.ICESNumber;
}


export interface ICostshareBulkUpdateTiers extends FAM.ITierLevel {
  primaryValue?: FAM.IEntityReferenceValue;
  pcpValue?: FAM.IEntityReferenceValue;
  specialistValue?: FAM.IEntityReferenceValue;
}

export interface IPlanBenefitRelation { benefitRef: FAM.IBenefitRef, plan: FAM.IPlan }

export interface IGCData {
  json: FAM.IGroupConfigurationData;
  config: FAM.IGroupConfigurationConfig;
  snapshot: IXrmTypes.bcbs_groupconfigurationsnapshot;
  opportunity?: IXrmTypes.opportunity;
  account?: IXrmTypes.account;
  bcbs_quoterequest?: IXrmTypes.bcbs_quoterequest[];
  bcbs_agencydetails?: Ibcbs_agencydetail_extended[];
  bcbs_reps?: ISystemUserExtended[];

  // used to hold the version of blob if snapshot(history record) is loaded
  snapshotVersionId?: string;

  // used to identify real data changes
  modifiedOn?: number;

  savedOn?: number;
  checkList?: ICheckList[];
  opportunityProduct?: FAM.IOpportunityProduct;
  notifications?: FAM.INotification[];
}

function defaultDataSlice(): IGCData {

  const defaultDataSlice: IGCData = {
    json: {} as FAM.IGroupConfigurationData,
    config: {} as FAM.IGroupConfigurationConfig,
    snapshot: {},
    opportunity: {},
    account: {},
    bcbs_quoterequest: undefined,
    bcbs_agencydetails: undefined,
    modifiedOn: undefined,
    savedOn: undefined,
    checkList: undefined,
    opportunityProduct: undefined,
    notifications: undefined,
  };

  return defaultDataSlice;
}

const dataSlice = createSlice({
  name: 'data',
  initialState: defaultDataSlice() as IGCData,
  reducers: {
    notificationAdd: (state, action: PayloadAction<Partial<FAM.INotification>>) => {
      if (!state.notifications)
        state.notifications = [];

      state.notifications.push({ id: action.payload.id!, name: action.payload.name!, description: action.payload.description! });
    },
    notificationRemove: (state, action: PayloadAction<string>) => {
      if (state.notifications)
        state.notifications = state.notifications.filter(t => t.id !== action.payload);
    },
    purgeOnData: (state) => {
      console.debug("[appl] dataSlice.purgeOnData")
      return { ...state, ...defaultDataSlice() };
    },
    updateSnapshotData: (state, action: PayloadAction<Partial<IGCData>>) => {
      return { ...state, ...action.payload, changeToken: Date.now() };
    },
    addUpdateGroupStructure: (state, action: PayloadAction<Partial<FAM.IGroupStructure>>) => {

      // any separate ces numbers provided?
      if (action.payload.cesNumbers) {
        action.payload.cesNumbers.forEach(eachCESNumber => {
          const index = state.json.groupStructure.cesNumbers.findIndex(w => w.id === eachCESNumber.id)

          if (-1 === index) {
            state.json.groupStructure.cesNumbers.push(eachCESNumber)
          }
          else {
            state.json.groupStructure.cesNumbers[index] = eachCESNumber;
          }
        });
      }


      // any departments provided?
      if (action.payload.departments) {
        action.payload.departments.forEach(eachDepartment => {
          const index = state.json.groupStructure.departments.findIndex(w => w.id === eachDepartment.id);

          // Department
          if (index > -1) {
            // found the group number?
            console.log('replacing group number', index, eachDepartment)
            state.json.groupStructure.departments[index] = eachDepartment;
          }
          else {
            // its a new group number?
            console.log('adding new group number', index, eachDepartment)
            state.json.groupStructure.departments.push(eachDepartment)
          }
        });
      }

      // any group numbers provided?
      if (action.payload.groupNumbers) {
        action.payload.groupNumbers.forEach(eachGroupNumber => {
          const index = state.json.groupStructure.groupNumbers.findIndex(w => w.id === eachGroupNumber.id);

          // Group Number Updates:
          if (index > -1) {
            // found the group number?
            console.log('replacing group number', index, eachGroupNumber)
            state.json.groupStructure.groupNumbers[index] = eachGroupNumber;
          }
          else {
            // its a new group number?
            console.log('adding new group number', index, eachGroupNumber)
            state.json.groupStructure.groupNumbers.push(eachGroupNumber)
          }
        });
      }

      state.modifiedOn = Date.now();

    },
    changeField: (state, action: PayloadAction<IFlexibleAttributePayload>) => {

      // in order to prevent loop, time to live, 
      // if it is more than 10, we are going to ignore the change
      if (action.payload.ttl && action.payload.ttl > 10)
        return;

      // we are going to use category to identify the origin of input and use scope to select index within array
      // - Group Configuration
      // - Benefit
      // - Plan
      // - Fees and Premiums

      const category = action.payload.category;
      let benefitCategory: FAM.IBenefit | null = null;

      if (category && category.bcbs_config_categoryid) {

        const scope = action.payload.scope as FAM.IPlan | FAM.IFeesAndPremiums | FAM.IBenefit | FAM.IGroupNumber | FAM.IAccountsReceivable

        let field: FAM.IFlexibleField | null = null;
        let fields: FAM.IFlexibleField[] | null = null;

        //TODO switch statement
        if (category.bcbs_config_categoryid === '4f432ecf-bc5d-ec11-8f8f-000d3a342d18') {

          // Group Configuration
          fields = state.json.fields ?? null;
          field = state.json.fields.find(q => q.id === action.payload.field!.id) ?? null;

        }
        else if (category.bcbs_config_categoryid === 'd33bde62-d55d-ec11-8f8f-000d3a342d18') {

          // Plan
          fields = state.json.plans.find(p => p.id === scope.id)?.fields ?? null;
          field = fields?.find(q => q.id === action.payload.field!.id) ?? null;
        }
        else if (category.bcbs_config_categoryid === '7c8c262c-d778-ec11-8d21-0022480b0e40') {

          // Fees And Premiums
          fields = state.json.feesAndPremiums.find(p => p.id === scope.id)?.fields ?? null;
          field = fields?.find(q => q.id === action.payload.field!.id) ?? null;

        }
        else if (category.bcbs_config_categoryid === '23153cda-d08d-ec11-b400-0022480b6b1d') {

          // sub groups
          fields = state.json.groupStructure.groupNumbers.find(p => p.id === scope.id)?.fields ?? null;
          field = fields?.find(q => q.id === action.payload.field!.id) ?? null;

        }
        else if (category.bcbs_config_categoryid === 'c1816640-d778-ec11-8d21-0022480b0e40') {

          // stop loss
          fields = state.json.stopLoss.find(p => p.id === scope.id)?.fields ?? null;
          field = fields?.find(q => q.id === action.payload.field!.id) ?? null;

        }
        else if (category.bcbs_config_categoryid === 'c4d3ccf4-b08d-ec11-b400-0022480b6b1d') {

          //claims AR
          fields = state.json.claimsARs.find(p => p.id === scope.id)?.fields ?? null;
          field = fields?.find(q => q.id === action.payload.field!.id) ?? null;

        }
        else if (category.bcbs_config_categoryid === 'a0c3f887-d28d-ec11-b400-0022480b6b1d') {
          // Non Standards ?
          const scope = action.payload.scope as unknown as FAM.INonStandard

          // Scope is: group configuration
          fields = state.json.nonStandards.find(p => p.id === scope.id)?.fields ?? null;
          field = fields?.find(q => q.id === action.payload.field!.id) ?? null;

        }
        else if (category.bcbs_config_categoryid === 'ed7b784a-38d0-ee11-9079-002248081a21') {
          // Scope is: Product Option Benefit
          fields = state.opportunityProduct?.options.find(p => p.id === scope.id)?.fields ?? null;
          field = fields?.find(q => q.id === action.payload.field!.id) ?? null;

        }
        else if (state.config.cache.bcbs_config_category_benefits.some(t => t.bcbs_config_categoryid === category.bcbs_config_categoryid)) {
          // Benefit
          fields = state.json.benefits.find(q => q.id === scope.id)?.fields ?? null;
          field = fields?.find(q => q.id === action.payload.field!.id) ?? null;
          benefitCategory = category as FAM.IBenefit;
        }

        // did we find the field we were looking for?
        if (field) {

          // lets find the value between tiers and primary/pcp values
          const fieldValue = field.values.find(t => t.id === action.payload.fieldValue.id);

          if (fieldValue) {
            if (action.payload.value) {
              const emitEvent = emitEventForFieldChange(
                state.config,
                state.snapshot,
                benefitCategory,
                field,
                fieldValue,
                action.payload.value);

              if (emitEvent)
                fieldValue.emitRefId = emitEvent?.bcbs_groupconfigurationsnapshoteventid;

              fieldValue.value = action.payload.value;

            } else {
              fieldValue.value = null;
            }
          }

          // Visibility Rules Processing
          if (field && fields) {

            const _fieldMap = ArrayToMapByPredicateWithMap(fields, eField => eField.configRef.id, (eField, index) => {
              const attribute = state.config.cache.map_bcbs_config_attribute[eField.configRef.id];
              const masterAttribute = state.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
              const ret: IFieldReference = {
                index: index,
                field: eField,
                attribute: attribute,
                masterAttribute: masterAttribute,
              }
              return ret;
            });


            const gProcessor = new ParentVisibilityGraphProcessor(state.config, state.config.cache.parentVisibilityGraph);
            const graphChanges = gProcessor.processVisibilityOnAttributeChange(state.config, field, fields, _fieldMap);

            graphChanges.forEach(eGraphChange => {
              if (fields)
                if (eGraphChange.newVisibility === 0) {

                  // TODO refactor, unify all three and clean up
                  // * Duplicated code, this has been duplicated in addUpdateAttributeSet, AttributeSetGridViewEditor.onChange, NonStandardGridViewEditor.onChange
                  delete fields[eGraphChange.index].visibility

                  // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/319161
                  fields[eGraphChange.index].values.forEach(eachValue => {

                    if (eGraphChange.attribute.bcbs_defaultvalue)
                      switch (eGraphChange.masterAttribute.bcbs_attributetypecode) {

                        // ignore, nothing to do, since it is of type message
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Message(808900014)'].value:
                          break;

                        // ignore, nothing to do, since it is of type message
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Lookup(808900006)'].value:
                          throw new Error("Not implemented exception!")
                          break;

                        case bcbs_config_masterattribute.bcbs_attributetypecode['Single Line of Text(808900008)'].value:
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Boolean(808900000)'].value:
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Option Set(808900007)'].value:
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Currency(808900002)'].value:
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Date(808900003)'].value:

                        case bcbs_config_masterattribute.bcbs_attributetypecode['Whole Number(808900010)'].value:
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Decimal(808900004)'].value:
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Percentage(808900005)'].value:

                        case bcbs_config_masterattribute.bcbs_attributetypecode['Costshare(808900001)'].value:
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Percentage(808900005)'].value:
                        case bcbs_config_masterattribute.bcbs_attributetypecode['Vision Costshare(808900012)'].value:

                          // blank string or no-value at all
                          if (
                            !eachValue.value ||
                            (eachValue.value.type === 'string' && eachValue.value.value === '')
                          ) {

                            const field = fields![eGraphChange.index];
                            const fieldValue = eachValue;

                            const value: FAM.TValue = friendlyValueToFlexibleFieldValue(
                              state.config,
                              eGraphChange.masterAttribute.bcbs_attributetypecode as EControlType,
                              eGraphChange.attribute.bcbs_defaultvalue!,
                              eGraphChange.attribute,
                              eGraphChange.masterAttribute,
                              benefitCategory,
                              null
                            );

                            eachValue.value = value

                            // deep clone action for new data
                            const nAction: PayloadAction<IFlexibleAttributePayload> = {
                              ...action,
                              payload: {
                                ...action.payload,
                                field: field,
                                fieldValue: fieldValue,
                                value: value,
                                ttl: (action.payload.ttl ?? 0) + 1
                              }
                            };

                            dataSlice.caseReducers.changeField(state, nAction);
                          }

                          break;

                        default:
                          break;
                      }

                  });
                }
                else {
                  fields[eGraphChange.index].visibility = eGraphChange.newVisibility
                }
            });

            const changeValue = action.payload.value;

            const cancellationFields = CancellationFieldsByCategory[category.bcbs_config_categoryid];

            const {
              "Cancellation Effective Date": cancellationEffectiveDateId,
              "Pay Run Out Claims How Many Months": payRunOutClaimsHowManyMonthsId,
              "Run Out Claims End Date": runOutClaimsEndDateId,
            } = cancellationFields ?? {};

            // Auto-calculate Run Out Claims End Date
            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/377166
            // Benefit Level
            if (field.configRef.id === cancellationEffectiveDateId || field.configRef.id === payRunOutClaimsHowManyMonthsId) {
              if (changeValue) {
                // Cancellation Effective Date
                // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=c9a8fddf-52a3-ec11-9840-0022480b1a5e
                let bcbs_cancellationeffectivedate = null as null | string;
                const bcbs_cancellationeffectivedate_fieldMap = _fieldMap.get(cancellationEffectiveDateId);
                if (bcbs_cancellationeffectivedate_fieldMap?.field.values?.[0].value?.type === 'date')
                  bcbs_cancellationeffectivedate = bcbs_cancellationeffectivedate_fieldMap.field.values[0].value.value;

                // Group Configuration: if this is root, use effective date configuration
                if (cancellationEffectiveDateId === '36a46909-d05d-ec11-8f8f-000d3a5a062b')
                  bcbs_cancellationeffectivedate = state.json.effectiveDate?.value!

                // Pay Run Out Claims How Many Months
                // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=cda8fddf-52a3-ec11-9840-0022480b1a5e
                const bcbs_payrunoutclaimsmonths_fieldMap = _fieldMap.get(payRunOutClaimsHowManyMonthsId);
                let bcbs_payrunoutclaimsmonths = null as null | number;
                if (bcbs_payrunoutclaimsmonths_fieldMap?.field.values?.[0].value?.type === 'number')
                  bcbs_payrunoutclaimsmonths = bcbs_payrunoutclaimsmonths_fieldMap?.field.values[0].value.value;

                // Run Out Claims End Date
                // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=cfa8fddf-52a3-ec11-9840-0022480b1a5e
                const bcbs_runoutclaimsenddate_fieldMap = _fieldMap.get(runOutClaimsEndDateId);

                if (bcbs_cancellationeffectivedate && bcbs_payrunoutclaimsmonths && bcbs_runoutclaimsenddate_fieldMap) {
                  const dateAPlusNMonths = addNMonthsToDateString(bcbs_cancellationeffectivedate, bcbs_payrunoutclaimsmonths);
                  {

                    const field = bcbs_runoutclaimsenddate_fieldMap.field;
                    const value: FAM.TValue = { type: 'date', value: dateAPlusNMonths };
                    const fieldValue = bcbs_runoutclaimsenddate_fieldMap.field.values[0];

                    // deep clone action for new data
                    const nAction: PayloadAction<IFlexibleAttributePayload> = {
                      ...action,
                      payload: {
                        ...action.payload,
                        field: field,
                        fieldValue: fieldValue,
                        value: value,
                        ttl: (action.payload.ttl ?? 0) + 1
                      }
                    };

                    // bcbs_runoutclaimsenddate_fieldMap.field.values[0].value = value
                    dataSlice.caseReducers.changeField(state, nAction);

                  }
                }
              }
            }



          }
        }
      }

      state.modifiedOn = Date.now();


    },
    changeProperty: (state, action: PayloadAction<IChangePropertyPayload>) => {

      // // eslint-disable-next-line no-restricted-globals
      // (parent as any).postMessage({ action: "onChange" }, "*");

      // TODO this is currently only used for opportunity product
      const option = state.opportunityProduct?.options.find(t => t.id === action.payload.scope.id)!

      const property = action.payload.property as keyof FAM.IOpportunityProductOptionBenefit;

      if (action.payload.value?.type === 'string')
        (option as any)[property] = action.payload.value.value;
      else if (action.payload.value?.type === 'number')
        (option as any)[property] = action.payload.value.value;
      else if (action.payload.value?.type === 'bool')
        (option as any)[property] = action.payload.value.value;
      else
        (option as any)[property] = null;

      if (option.deactivated)
        option.won = false;


      const useBaseAdmin = isBaseAdminQuoteTypeCode(state.opportunityProduct?.bcbs_opportunityproduct.bcbs_quotetypecode)

      if (useBaseAdmin) {
        option.bcbs_buyupuwsinglei = 0;
        option.bcbs_buyupuwfamilyi = 0;
        option.bcbs_buyupuweechildreni = 0;
        option.bcbs_buyupuweespousei = 0;
        option.bcbs_buyupuwsinglem = 0;
        option.bcbs_buyupuwfamilym = 0;
        option.bcbs_buyupuweechildrenm = 0;
        option.bcbs_buyupuweespousem = 0;
      }


      // recalculating the premium
      if (!useBaseAdmin)
        option.bcbs_buyupuwtotali = 0
          + (option.bcbs_buyupuwsinglei ?? 0)
          + (option.bcbs_buyupuwfamilyi ?? 0)
          + (option.bcbs_buyupuweechildreni ?? 0)
          + (option.bcbs_buyupuweespousei ?? 0);

      if (!useBaseAdmin)
        option.bcbs_buyupuwtotalm = 0
          + ((option.bcbs_buyupuwsinglei ?? 0) * (option.bcbs_buyupuwsinglem ?? 0))
          + ((option.bcbs_buyupuwfamilyi ?? 0) * (option.bcbs_buyupuwfamilym ?? 0))
          + ((option.bcbs_buyupuweechildreni ?? 0) * (option.bcbs_buyupuweechildrenm ?? 0))
          + ((option.bcbs_buyupuweespousei ?? 0) * (option.bcbs_buyupuweespousem ?? 0));

      option.bcbs_annualpremium = (option.bcbs_buyupuwtotalm ?? 0) * 12;
    },
    setSaveTokenOnData: (state) => {
      state.savedOn = Date.now();
    },
    addUpdateAttributeSet: (state, action: PayloadAction<IAttributeSetPayload[]>) => {
      action.payload.forEach(eachPayload => {

        const { config, scope, attributeSets } = eachPayload;

        let target:
          // FAM.IGroupConfigurationData |
          // FAM.IPlan |
          FAM.IFeesAndPremiums |
          FAM.IBenefit |
          // FAM.IGroupNumber |
          FAM.IStopLoss |
          FAM.IAccountsReceivable |
          null |
          undefined = null;


        let benefitCategory: FAM.IBenefit | null = null;

        if (eachPayload.category) {
          const category = eachPayload.category;
          const categoryMapByName = config.cache.map_bcbs_config_category_named;

          if (category.bcbs_config_categoryid === categoryMapByName['Group Configuration'].bcbs_config_categoryid) {
            // Group Configuration
            // target = state.data;
            throw new Error('AttributeSet update has not been implemented for (Group Configuration).');

          }
          else if (category.bcbs_config_categoryid === categoryMapByName['Plan'].bcbs_config_categoryid) {
            // Plan
            // target = state.data.plans.find(q => q.id === scope.id);
            throw new Error('AttributeSet update has not been implemented for plan.');

          }
          else if (category.bcbs_config_categoryid === categoryMapByName['Fees and Premiums'].bcbs_config_categoryid) {
            // Fees And Premiums
            target = state.json.feesAndPremiums.find(p => p.id === scope.id);


          } else if (category.bcbs_config_categoryid === categoryMapByName['Group Number'].bcbs_config_categoryid) {
            // sub groups
            // not implemented, this would be grid in a grid
            // target = state.data.groupStructure.groupNumbers.find(p => p.id === scope.id)
            throw new Error('AttributeSet update has not been implemented for group-structure.');

          } else if (category.bcbs_config_categoryid === categoryMapByName['Stop Loss'].bcbs_config_categoryid) {
            // stop loss
            target = state.json.stopLoss.find(p => p.id === scope.id);

          } else if (category.bcbs_config_categoryid === categoryMapByName['Accounts Receivable'].bcbs_config_categoryid) {
            // claims AR
            target = state.json.claimsARs.find(p => p.id === scope.id);

          } else if (config.cache.bcbs_config_category_benefits.some(q => q.bcbs_config_categoryid === category.bcbs_config_categoryid)) {
            // Benefit
            target = state.json.benefits.find(p => p.id === scope.id);
            benefitCategory = target as FAM.IBenefit;
          } else if (category.bcbs_config_categoryid === categoryMapByName['Non-Standard'].bcbs_config_categoryid) {
            // non-standards
            // not implemented, this would be grid in a grid
            throw new Error('AttributeSet update has not been implemented for non-standards.')

          } else {
            throw new Error('AttributeSet update failed to identify resource.');
          }
        }

        if (target && target?.attributeSets) {

          attributeSets.forEach(eachAttributeSet => {

            if (eachPayload.delete) {
              const index = target?.attributeSets.findIndex(w => w.id === eachAttributeSet.id);
              if (index !== undefined && index > -1) {
                target!.attributeSets.splice(index, 1);
              }
              return;
            }

            const scope = BuildVisibilityScope(
              null,
              state.json,
              null,
              null
            );

            ApplyVisibilityFields(state.config, eachAttributeSet.fields, scope);

            const index = target?.attributeSets.findIndex(w => w.id === eachAttributeSet.id);

            EmitForChangeTracker(config, state.snapshot, eachPayload.changeTracker, eachAttributeSet.fields, benefitCategory);

            if (index !== undefined && index > -1) {
              target!.attributeSets[index] = eachAttributeSet;
            } else {
              //@ts-ignore
              target.attributeSets.push(eachAttributeSet);
            }

            // Fees and Premiums - Shared Savings - New fields in Shared Savings Attribute Set
            // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/516907

            // Shared Savings / Attribute Set
            // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=296bac98-575e-ee11-be6f-000d3a3bec27

            // TODO if multiple fire only for distinct fees and premium...
            if (eachAttributeSet.attributeSetRef.id === "296bac98-575e-ee11-be6f-000d3a3bec27") {

              const config = state.config;
              const theFNP = state.json.feesAndPremiums.find(t => t.id === target?.id)!;
              const stateFeesAndPremiums = state.json.feesAndPremiums;
              const stateGroupNumbers = state.json.groupStructure.groupNumbers;
              const statePlans = state.json.plans;
              const stateClaimsARs = state.json.claimsARs;

              const autoCalculateResult = AutoCalculate(stateFeesAndPremiums, stateGroupNumbers, statePlans, stateClaimsARs, config, theFNP);

              autoCalculateResult.errors.forEach(eachError => {

                dataSlice.caseReducers.notificationAdd(state, {
                  type: '',
                  payload: {
                    id: eachAttributeSet.id,
                    name: "",
                    description: eachError
                  }
                });

              });
            }

          });
        } else {
          throw new Error('Target has no attribute set setup');
        }

      });

      state.modifiedOn = Date.now();

    },
    changeBulkCostshare: (state, action: PayloadAction<any>) => {

      const { costshareFields, costshareValues } = action.payload;

      const scope = action.payload.currentBenefit as FAM.IBenefit

      costshareFields.forEach((csField: FAM.IFlexibleField) => { //for each costshare field in snapshot that accepts default value
        let field: FAM.IFlexibleField | null = null;
        field = state.json.benefits.find(b => b.id === scope.id)!.fields.find(q => q.id === csField!.id)!
        costshareValues.forEach((csVal: ICostshareBulkUpdateTiers) => {  //for each tier
          if (csVal.primaryValue) {  //if primary
            const value = csVal.primaryValue
            if (field) {
              let fieldValue = field!.values!.find(v => v.tierRef?.id === csVal?.id && v.type === 'primary')
              if (fieldValue)
                fieldValue.value = value
            }

          }
          if (csVal.pcpValue) { //if pcp
            const value = csVal.pcpValue
            if (field) {
              let fieldValue = field!.values!.find(v => v.tierRef?.id === csVal?.id && v.type === 'pcp')
              if (fieldValue)
                fieldValue.value = value
            }

          }
          if (csVal.specialistValue) { //if specialist
            const value = csVal.specialistValue
            if (field) {
              let fieldValue = field!.values!.find(v => v.tierRef?.id === csVal?.id && v.type === 'specialist')
              if (fieldValue)
                fieldValue.value = value
            }

          }
        })
      });

      state.modifiedOn = Date.now();

    },
    addUpdateNonStandards: (state, action: PayloadAction<INonStandardPayload[]>) => {

      action.payload.forEach(eachPayload => {

        if (eachPayload.groupConfiguration) {

          eachPayload.nonStandards.forEach(eachNonStandard => {

            applyVisibilityNonstandard(state.json, state.config, null, eachPayload.benefit ?? null, eachNonStandard);

            const nonstandardIndex = state.json.nonStandards.findIndex(q => q.id === eachNonStandard.id);

            EmitForChangeTracker(state.config, state.snapshot, eachPayload.changeTracker, eachNonStandard.fields, null);

            if (nonstandardIndex > -1) {
              state.json.nonStandards[nonstandardIndex] = eachNonStandard;
            } else {
              state.json.nonStandards.push(eachNonStandard);
            }
          })
        }

        if (eachPayload.benefit) {
          eachPayload.nonStandards.forEach(eachNonStandard => {
            applyVisibilityNonstandard(state.json, state.config, null, eachPayload.benefit ?? null, eachNonStandard);

            const benefitIndex = state.json.benefits.findIndex(q => q.id === eachPayload.benefit?.id)!;
            const nonstandardIndex = state.json.benefits[benefitIndex].nonStandards.findIndex(q => q.id === eachNonStandard.id);

            EmitForChangeTracker(
              state.config,
              state.snapshot,
              eachPayload.changeTracker,
              eachNonStandard.fields,
              state.json.benefits[benefitIndex]
            );

            if (nonstandardIndex > -1) {
              state.json.benefits[benefitIndex].nonStandards[nonstandardIndex] = eachNonStandard;
            } else {
              state.json.benefits[benefitIndex].nonStandards.push(eachNonStandard);
            }
          });
        }



      });

      state.modifiedOn = Date.now();
    },
    addUpdateNonStandardTemplates: (state, action: PayloadAction<INonStandardTemplatePayload[]>) => {
      action.payload.forEach(eachPayload => {
        eachPayload.nonStandardTemplates.forEach(eachNonStandardTemplate => {
          applyVisibilityNonstandardTemplate(state.json, state.config, null, null, eachNonStandardTemplate);
          const nonstandardTemplateIndex = state.json.nonStandardTemplates.findIndex(q => q.id === eachNonStandardTemplate.id);
          console.log(eachNonStandardTemplate);
          if (nonstandardTemplateIndex > -1) {
            state.json.nonStandardTemplates[nonstandardTemplateIndex] = eachNonStandardTemplate;
          } else {
            state.json.nonStandardTemplates.push(eachNonStandardTemplate);
          }
        });
      });
    },
    addUpdateGroupConfiguration: (state, action: PayloadAction<IGroupConfigurationUpdate>) => {

      console.log("[appl] dataSlice.addUpdateGroupConfiguration instructions", action.payload);

      if (action.payload) {
        const groupConfigurationData = action.payload.groupConfigurationChanges;
        const xFundingType = state.json.fundingType;

        // Funding Type updates
        // -----------------------
        // if there was a previous value
        if (xFundingType?.value)
          if (action.payload.instructions?.fundingTypeChanged && groupConfigurationData.fundingType) {

            if (groupConfigurationData.fundingType?.value !== xFundingType.value) {

              console.warn(`Changing funding type to: ${groupConfigurationData.fundingType?.name}\nSome fields may be cleared after applying visibility rules.`);

              state.json.benefits.forEach(benefit => {

                let update = false;

                // matches old funding type?
                if (action.payload.instructions?.fundingTypeUpdateMatching)
                  if (benefit.fundingType?.value === xFundingType.value)
                    update = true;

                // if asked to update non-matching, technically everything at the moment
                if (action.payload.instructions?.fundingTypeUpdateNonMatching)
                  if (benefit.fundingType?.value !== xFundingType.value)
                    update = true;

                // if not dual funded, update everything
                if (groupConfigurationData.dualFunded?.value === false)
                  update = true;

                // Update the process that syncs funding type down to lower levels on change at the Group Configuration level to not update the funding type on any benefits identified as a product.
                // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/494173
                if (benefit.product?.value === FAM.EProduct.Yes) {
                  update = false;
                }

                // update only if it has a value/category allows to update
                // more proper way would be to look at category and if it is setup for funding type, then set the value
                if (update && benefit.fundingType?.value)
                  benefit.fundingType = { ...groupConfigurationData.fundingType! };

              });

              state.json.plans.forEach(plan => {

                let update = false;

                // matches old funding type?
                if (action.payload.instructions?.fundingTypeUpdateMatching)
                  if (plan.fundingType?.value === xFundingType.value)
                    update = true;

                // if asked to update non-matching, technically this means everything at the moment
                if (action.payload.instructions?.fundingTypeUpdateNonMatching)
                  if (plan.fundingType?.value !== xFundingType.value)
                    update = true;

                // if not dual funded, update everything
                if (groupConfigurationData.dualFunded?.value === false)
                  update = true;

                // update only if it has a value/category allows to update
                if (update && plan.fundingType?.value)
                  plan.fundingType = { ...groupConfigurationData.fundingType! };

              });
            }
          }

        // taking care of snapshot properties
        for (const t of Object.entries(groupConfigurationData)) {
          const [key, value] = t // [keyof FAM.IGroupConfigurationData, any]

          //TODO not sure how to tell typescript about the keys
          //@ts-ignore
          state.json[key] = value;
        }

        let runVisibility = true;

        if (action?.payload?.instructions?.ignoreVisibilityRun)
          runVisibility = false;

        // TODO more conditions to follow

        if (runVisibility)
          // for group configuration we need to do a full run on all entities
          applyVisibility(state.json, state.config, undefined);
      }

      state.modifiedOn = Date.now();

    },
    addUpdateGroupConfigurationShowInActives: (state, action: PayloadAction<FAM.IBooleanValue>) => {
      state.json.showInActives = action.payload
        ?? {
        type: 'bool',
        value: false
      };
    },
    addUpdateBenefit: (state, action: PayloadAction<FAM.IBenefit[]>) => {
      console.log('adding benefit', action);
      action.payload.forEach(eachBenefit => {

        const index = state.json.benefits.findIndex(w => w.id === eachBenefit.id);

        // if not dual funded, then match benefits funding type with
        if (!state.json.dualFunded?.value)
          if (eachBenefit.fundingType?.value !== state.json.fundingType?.value) {
            eachBenefit.fundingType = { ...state.json.fundingType! };
            console.debug('[appl] funding type enforced', eachBenefit.fundingType);
          }

        applyVisibility(state.json, state.config, undefined, { benefit: eachBenefit });

        if (eachBenefit.deactivated) {
          eachBenefit.name = `${eachBenefit.name.trim()} - deactivated(${DateToYYYYMMDDHHMMSS(new Date())})`;
        }
        else {
          const nameTest = eachBenefit.name.match(/(.*)\s-\sdeactivated\(\d*\)$/i);
          if (nameTest?.length === 2) {
            eachBenefit.name = nameTest[1].trim();
          }
        }

        if (index > -1) {
          console.log('replacing benefit', index, eachBenefit)
          emitEventForBenefitPropertyChange(state.snapshot, state.json.benefits[index], eachBenefit);
          state.json.benefits[index] = eachBenefit;
        }
        else {
          console.log('adding new benefit', index, eachBenefit)
          state.json.benefits.push(eachBenefit)
        }


        state.json.plans.forEach(eachPlan => {
          for (var i = 0; i < eachPlan.benefitRefs.length; i++) {
            const eachPlanMatchingBenefitRef = eachPlan.benefitRefs[i];
            if (eachPlanMatchingBenefitRef.id === eachBenefit.id) {
              eachPlanMatchingBenefitRef.name = eachBenefit.name;

              if (eachBenefit.deactivated) {
                eachPlan.benefitRefs.splice(i, 1);
              }
            }
          }
        })
      });

      state.modifiedOn = Date.now();

    },
    //TODO merge this two
    addUpdateStopLoss: (state, action: PayloadAction<IAddFeesPayload[]>) => {
      console.log('adding stoploss', action);
      action.payload.forEach(t => {
        const { stopLoss: eachStopLoss, feesAndPremiums } = t;

        applyVisibility(state.json, state.config, undefined, { stopLoss: eachStopLoss });

        const index = state.json.stopLoss.findIndex(w => w.id === eachStopLoss.id);

        if (index > -1) {
          console.log('replacing stoploss', index, eachStopLoss)
          state.json.stopLoss[index] = eachStopLoss;
        }
        else {
          console.log('adding new stoploss', index, eachStopLoss)
          state.json.stopLoss.push(eachStopLoss)
        }

        // if there is a fee association, lets add
        if (feesAndPremiums?.id) {
          console.log('linking feesAndPremiums', index, eachStopLoss)

          const fNP = state.json.feesAndPremiums.find(t => t.id === feesAndPremiums!.id)!;
          fNP.stopLossRef = {
            id: eachStopLoss.id,
            name: eachStopLoss.name
          };
        };

        // renaming
        state.json?.groupStructure?.groupNumbers?.forEach(eachGN => {
          if (eachGN.stopLossRef?.id === eachStopLoss.id) {
            eachGN.stopLossRef.name = eachStopLoss.name;

            if (eachStopLoss.deactivated)
              eachGN.stopLossRef = null;

          }
        });

      });

      state.modifiedOn = Date.now();
    },
    addUpdateAccountsReceivable: (state, action: PayloadAction<FAM.IAccountsReceivable[]>) => {

      console.log('adding/updating AccountReceivable', action);
      action.payload.forEach(eachAccountReceivable => {

        applyVisibility(state.json, state.config, undefined, { accountReceivable: eachAccountReceivable });

        const index = state.json.claimsARs.findIndex(w => w.id === eachAccountReceivable.id);

        if (index > -1) {
          console.log('replacing benefit', index, eachAccountReceivable)
          state.json.claimsARs[index] = eachAccountReceivable;
        }
        else {
          // if we can't find claimsAR, add to array
          console.log('adding new benefit', index, eachAccountReceivable)
          state.json.claimsARs.push(eachAccountReceivable)
        }

        // renaming
        state.json?.groupStructure?.groupNumbers?.forEach(eachGN => {
          if (eachGN.accountsReceivableRef?.id === eachAccountReceivable.id) {
            eachGN.accountsReceivableRef.name = eachAccountReceivable.name;

            if (eachAccountReceivable.deactivated)
              eachGN.accountsReceivableRef = null;

          }
        });

      });

      state.modifiedOn = Date.now();

    },
    addUpdatePlan: (state, action: PayloadAction<FAM.IPlan[]>) => {
      action.payload.forEach(eachPlan => {

        if (!state.json.dualFunded?.value)
          if (eachPlan.fundingType?.value !== state.json.fundingType?.value) {
            eachPlan.fundingType = { ...state.json.fundingType! };
          }

        applyVisibility(state.json, state.config, undefined, { plan: eachPlan });

        const index = state.json.plans.findIndex(t => t.id === eachPlan.id);
        // if we find the plan, lets replace it, if we dont find the plan we will add
        if (index > -1) {
          console.log('replacing plan', eachPlan, index);
          state.json.plans[index] = eachPlan;
        }
        else {
          console.log('adding plan', eachPlan);
          state.json.plans.push(eachPlan);
        }

        // renaming plans
        state.json?.groupStructure?.groupNumbers?.forEach(eachGN => {
          if (eachGN.planRef?.id === eachPlan.id) {
            eachGN.planRef.name = eachPlan.name;

            if (eachPlan.deactivated)
              eachGN.planRef = null;
          }
        });

        // if (eachPlan.deactivated)
        //   eachPlan.benefitRefs = [];

      });

      state.modifiedOn = Date.now();

    },
    addBenefitReference: (state, action: PayloadAction<IPlanBenefitRelation[]>) => {
      console.log('adding benefit reference', action);
      action.payload.forEach(({ benefitRef, plan }) => {
        state.json.plans.find(t => t.id === plan.id)?.benefitRefs.push(benefitRef);
      });

      state.modifiedOn = Date.now();

    },
    removeBenefitReference: (state, action: PayloadAction<{ benefitRef: FAM.IBenefitRef, planRef: FAM.IPlan }>) => {
      const benefitRef = action.payload.benefitRef;
      const PlanRef = action.payload.planRef;
      const planBenefitRefs = state.json.plans.find(t => t.id === PlanRef.id)?.benefitRefs;
      const index = planBenefitRefs?.findIndex(q => q.id === benefitRef.id);
      if (index !== undefined)
        planBenefitRefs?.splice(index, 1);

      state.modifiedOn = Date.now();

    },
    updateFeesAndPremiums: (state, action: PayloadAction<FAM.IFeesAndPremiums[]>) => {
      state.json.feesAndPremiums = action.payload;
      state.modifiedOn = Date.now();
    },
    updatePlanFeesAndPremiums: (state, action: PayloadAction<{ planRef: FAM.IPlan, feesAndPremiumRef: FAM.IFeesAndPremiums }>) => {
      state.json.plans.find(t => t.id === action.payload.planRef.id)!.feesAndPremiumsRef = {
        id: action.payload.feesAndPremiumRef.id,
        name: action.payload.feesAndPremiumRef.name
      };

      state.modifiedOn = Date.now();

    },
    removePlanFeesAndPremiumsReference: (state, action: PayloadAction<{ planRef: FAM.IPlan }>) => {
      const plan = state.json.plans.find(t => t.id === action.payload.planRef.id);
      if (plan)
        plan.feesAndPremiumsRef = null;

      state.modifiedOn = Date.now();

    },
    addUpdateFeesAndPremiums: (state, action: PayloadAction<FAM.IFeesAndPremiums[]>) => {
      action.payload.forEach(eachFeesAndPremium => {

        applyVisibility(state.json, state.config, undefined, { feesAndPremium: eachFeesAndPremium });

        const index = state.json.feesAndPremiums.findIndex(t => t.id === eachFeesAndPremium.id);

        // if we find the plan, lets replace it, if we dont find the plan we will add
        if (index > -1) {
          console.log('replacing FeesAndPremiums', eachFeesAndPremium, index);
          state.json.feesAndPremiums[index] = eachFeesAndPremium;
        }
        else {
          console.log('adding FeesAndPremiums', eachFeesAndPremium);
          state.json.feesAndPremiums.push(eachFeesAndPremium);
        };

        state.json.plans.forEach(eachPlan => {
          if (eachPlan.feesAndPremiumsRef?.id === eachFeesAndPremium.id) {

            eachPlan.feesAndPremiumsRef.name = eachFeesAndPremium.name

            if (eachFeesAndPremium.deactivated) {
              console.log('removing a fee reference for a deactivated fee.', eachPlan, eachFeesAndPremium);
              eachPlan.feesAndPremiumsRef = null;
            }

          }
        });

      });

      state.modifiedOn = Date.now();

    },
    updateChangeType: (state, action: PayloadAction<FAM.IOptionSetValue>) => {
      state.json.changeType = action.payload;
      state.modifiedOn = Date.now();
    },
    importPlansAndBenefits: (state, action: PayloadAction<{ plans?: FAM.IPlan[], benefits?: FAM.IBenefit[], config: FAM.IGroupConfigurationConfig }>) => {
      // const copyClone = copyClonePlan(config, plans, benefits);

      // if (copyClone.benefits)
      //   copyClone.benefits.forEach(q => state.data.benefits.push(q));

      // if (copyClone.plans)
      //   copyClone.plans.forEach(q => state.data.plans.push(q));


      action.payload.benefits?.forEach(eachBenefit => {
        // unique id verification
        const foundBenefit = state.json.benefits.find(q => q.id === eachBenefit.id);
        if (foundBenefit)
          throw new Error('Unexpected state, Another plan with same id found!');


        state.json.benefits.push(eachBenefit);
      });

      action.payload.plans?.forEach(eachPlan => {
        // unique id verification
        const foundPlan = state.json.plans.find(q => q.id === eachPlan.id);
        if (foundPlan)
          throw new Error('Unexpected state, Another plan with same id found!');

        state.json.plans.push(eachPlan);

        // Checking Benefit References
        if (eachPlan.benefitRefs && eachPlan.benefitRefs.length > 0)
          for (let i = eachPlan.benefitRefs.length - 1; i >= 0; i--) {
            let eachPlanBenefitRef = eachPlan.benefitRefs[i];

            const match = state.json.benefits.find(q => q.id === eachPlanBenefitRef.id);

            // benefit was not found? lets remove the reference
            if (!match)
              eachPlan.benefitRefs.splice(i, 1);
          }

        // Checking Fee And Premium Reference
        if (eachPlan.feesAndPremiumsRef) {
          const match = state.json.feesAndPremiums.find(q => q.id === eachPlan.feesAndPremiumsRef?.id);
          if (!match)
            eachPlan.feesAndPremiumsRef = null;
        }

      });

      state.modifiedOn = Date.now();

    },
    importPlanBenefits: (state, action: PayloadAction<{ plan?: FAM.IPlan, benefits: FAM.IBenefit[], config: FAM.IGroupConfigurationConfig }>) => {
      // clone all passed in benefits
      const benefits = action.payload.benefits.map(q => cloneBenefit(`${q.name} - Imported`, q));

      benefits.forEach(q => state.json.benefits.push(q));

      if (action.payload.plan) {
        //TODO: add validation for category
        //TODO: add more functions and breakdown category addition with validation
        const plan = state.json.plans.find(q => q.id === action.payload.plan?.id);

        if (plan)
          benefits.forEach(q => plan?.benefitRefs.push({ id: q.id, name: q.name }));
      }

      state.modifiedOn = Date.now();

    },
    toggleCommentStatus: (state, action: PayloadAction<IComment>) => {
      const elementIndex = state.json.comments.findIndex(element => element.id === action.payload.id);
      state.json.comments[elementIndex] = { ...action.payload, status: action.payload.status === 2 ? 1 : 2 };

      state.modifiedOn = Date.now();

    },
    updateCESNumber: (state, action: PayloadAction<IGroupStructureCESWizardItem[]>) => {

      const newCES = action.payload.filter(t => t.newCES).map(t => t.newCES!);

      // CES Number
      state.json.groupStructure.cesNumbers = newCES;

      // Departments
      state.json.groupStructure.departments.forEach((eachDepartment, eachDepartmentIndex) => {

        let match: IGroupStructureCESWizardItem | undefined = undefined

        // searching by ID first
        // searching by CESNumber second
        if (eachDepartment.cesNumberRef?.id)
          match = action.payload.find(t => t.oldCES?.id === eachDepartment.cesNumberRef?.id);
        else if (eachDepartment.cesNumberRef?.name)
          match = action.payload.find(t => t.oldCES?.cesNumber === eachDepartment.cesNumberRef?.name);

        if (match)
          eachDepartment.cesNumberRef = {
            ...eachDepartment.cesNumberRef,
            id: match.newCES.id,
            name: match.newCES.cesNumber,
          };

      });

      // Group Numbers
      state.json.groupStructure.groupNumbers.forEach((eachGroupNumber, tIndex) => {
        let match: IGroupStructureCESWizardItem | undefined = undefined

        // searching by ID first
        // searching by CESNumber second
        if (eachGroupNumber.cesNumberRef?.id)
          match = action.payload.find(t => t.oldCES?.id === eachGroupNumber.cesNumberRef?.id);
        else if (eachGroupNumber.cesNumberRef?.name)
          match = action.payload.find(t => t.oldCES?.cesNumber === eachGroupNumber.cesNumberRef?.name);

        if (match)
          eachGroupNumber.cesNumberRef = {
            ...eachGroupNumber.cesNumberRef,
            id: match.newCES.id,
            name: match.newCES.cesNumber,
          };

      });

      // Reconciling CES Number
      // anyCESnumber => firstCesNumber
      const mapping: [FAM.ICESNumber, FAM.ICESNumber][] = [];

      state.json.groupStructure.cesNumbers.forEach(eachCESNumber => {
        const goodCes = state.json.groupStructure.cesNumbers.find(t => t.cesNumber === eachCESNumber.cesNumber && !t.deactivated);

        if (goodCes && goodCes !== eachCESNumber) {
          mapping.push([eachCESNumber, goodCes])
        }

      });


      state.json.groupStructure.groupNumbers.forEach(eachGroupNumber => {
        const match = mapping.find(([badCES, _]) => badCES.id === eachGroupNumber?.cesNumberRef?.id);
        if (match) {
          const [_, goodCes] = match;
          eachGroupNumber.cesNumberRef = { id: goodCes.id, name: goodCes.cesNumber }
        }
      })

      state.json.groupStructure.departments.forEach(eachDepartment => {
        const match = mapping.find(([badCES, _]) => badCES.id === eachDepartment?.cesNumberRef?.id);
        if (match) {
          const [_, goodCes] = match;
          eachDepartment.cesNumberRef = { id: goodCes.id, name: goodCes.cesNumber }
        }
      })

      //
      state.json.groupStructure.cesNumbers = state.json.groupStructure.cesNumbers.filter(eachCESNumber => !mapping.some(([badCES, _]) => badCES === eachCESNumber))


      state.modifiedOn = Date.now();

    },
    updateModifiedOn: (state, action: PayloadAction<number>) => {
      state.modifiedOn = action.payload;
    },
    updateCheckList: (state, action: PayloadAction<ICheckList[]>) => {
      action.payload.forEach(eachChecklist => {
        const found = state.checkList?.find(t => t.bcbs_emchecklist.bcbs_emchecklistid === eachChecklist.bcbs_emchecklist.bcbs_emchecklistid);
        if (found)
          found.bcbs_emchecklist = eachChecklist.bcbs_emchecklist;
        else
          state.checkList?.push(eachChecklist);
      });
    },
    updateIteration: (state) => {

      const bcbs_config_release = state.config.bcbs_config_release!;

      state.json.iteration = blankSnapshot().iteration;

      state.json.releaseRef = {
        id: bcbs_config_release.bcbs_config_releaseid!,
        name: bcbs_config_release.bcbs_name!,
      }

      state.json.releaseVersion = bcbs_config_release.bcbs_versionnumber;

      console.log('[appl] dataSlice.updateIteration', {
        iteration: state.json.iteration,
        release: {
          name: bcbs_config_release.bcbs_name,
          id: bcbs_config_release.bcbs_config_releaseid,
          bcbs_versionnumber: bcbs_config_release.bcbs_versionnumber
        },
        jsonReleaseVersion: state.json.releaseVersion
      });

    },
    updateNotifications: (state, action: PayloadAction<Partial<FAM.INotification>[]>) => {

      const payloadRequest = action.payload.map(t => ({ status: 0, notification: t }));

      const notifications = state.json.notifications?.map(eachNotification => {
        const foundNotification = payloadRequest.find(t => t.notification.id === eachNotification.id);

        if (foundNotification) {
          eachNotification = { ...eachNotification, ...foundNotification.notification }
          foundNotification.status = 1;
        }
        return eachNotification;
      }) ?? [];

      state.json.notifications = notifications;
      state.modifiedOn = Date.now();
    },
    reconcileCESNumbers: (state, action: PayloadAction<undefined>) => {

    },

    addUpdateOpportunityProductOption: (state, action: PayloadAction<{ tierChange: ITierChange }>) => {
      if (!state.opportunityProduct?.options) {
        throw new Error("Unexpected state, opportunityProductData.options is not defined")
      }

      // brand new
      const option = createOpportunityOptionAndBenefits(
        state.config,
        state.opportunityProduct.bcbs_quoterequest,
        state.opportunityProduct.bcbs_opportunityproduct,
        undefined,
        undefined,
        {
          tierLevels: action.payload.tierChange.tiers!,
          tierSet: action.payload.tierChange.tierSet!,
        },
      );

      state.opportunityProduct.options.push(option);

      // TODO this is a hack, we need to find a better way to scroll
      // TODO we need to move this out of data layer
      setTimeout(() => {
        const element = document.getElementById("anchor-" + option.id);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    },
    cloneOpportunityProductOption: (state, action: PayloadAction<string>) => {
      const guid = action.payload;

      if (state.opportunityProduct?.options) {
        const stateOption = state.opportunityProduct.options.find(t => t.id === guid);

        // cloning from state
        const option = createOpportunityOptionAndBenefits(
          state.config,
          state.opportunityProduct.bcbs_quoterequest,
          state.opportunityProduct.bcbs_opportunityproduct,
          undefined,
          stateOption
        );

        state.opportunityProduct.options.push(option);

        // TODO this is a hack, we need to find a better way to scroll
        // TODO we need to move this out of data layer
        setTimeout(() => {
          const element = document.getElementById("anchor-" + option.id);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);

      }

    },

  },
})



// Action creators are generated for each case reducer function
export const {
  purgeOnData,
  updateSnapshotData,
  changeField,
  addUpdateAttributeSet,
  changeBulkCostshare,
  addUpdateBenefit,
  addBenefitReference,
  removeBenefitReference,
  removePlanFeesAndPremiumsReference,
  importPlanBenefits,
  addUpdatePlan,
  addUpdateStopLoss,
  addUpdateAccountsReceivable,
  addUpdateNonStandards,
  addUpdateNonStandardTemplates,
  updateCESNumber,
  updateFeesAndPremiums,
  updatePlanFeesAndPremiums,
  addUpdateFeesAndPremiums,
  addUpdateGroupStructure,
  importPlansAndBenefits,
  updateChangeType,
  addUpdateGroupConfiguration,
  addUpdateGroupConfigurationShowInActives,
  toggleCommentStatus,
  setSaveTokenOnData,
  updateModifiedOn,
  updateCheckList,
  updateIteration,
  updateNotifications,
  reconcileCESNumbers,
  addUpdateOpportunityProductOption,
  cloneOpportunityProductOption,
  changeProperty,
  notificationAdd,
  notificationRemove
} = dataSlice.actions

export default dataSlice.reducer



// TODO move this to emit event module
function EmitForChangeTracker(
  config: FAM.IGroupConfigurationConfig,
  snapshot: IXrmTypes.bcbs_groupconfigurationsnapshot,
  changeTracker: TChangeTracker,
  fields: FAM.IFlexibleField[],
  benefit: FAM.IBenefit | null,
) {
  const fieldsMap = ArrayToLookupByPredicate(fields, q => q.configRef.id);

  for (const [key, ct] of Object.entries(changeTracker)) {
    const targetField = fieldsMap[ct.field.configRef.id];
    const targeFieldValue = targetField.values.find(q => q.type === ct.fieldValue.type && q.tierRef?.id === ct.fieldValue?.tierRef?.id);

    if (targeFieldValue) {
      const emit = emitEventForFieldChange(
        config,
        snapshot,
        benefit,
        targetField,
        targeFieldValue,
        ct.value,
        false,
        true);

      // lets track the emitted event, so we can update later
      if (emit?.bcbs_groupconfigurationsnapshoteventid)
        targeFieldValue.emitRefId = emit.bcbs_groupconfigurationsnapshoteventid;
    }
  }
}


export const AutoCalculate = (
  stateFeesAndPremiums: FAM.IFeesAndPremiums[],
  stateGroupNumbers: FAM.IGroupNumber[],
  statePlans: FAM.IPlan[],
  stateClaimsARs: FAM.IAccountsReceivable[],
  config: FAM.IGroupConfigurationConfig,
  theFNP?: FAM.IFeesAndPremiums,
) => {

  const retResult = {
    errors: [] as string[],
  }

  const plansLookup = ArrayToLookupByPredicateWithMap(
    statePlans,
    e => e.id,
    e => e,
  );

  const accountsReceivableLookup = ArrayToLookupByPredicateWithMap(
    stateClaimsARs,
    e => e.id,
    e => e,
  );

  const feesAndPremiumsLookup = ArrayToLookupByPredicateWithMap(
    stateFeesAndPremiums,
    e => e.id,
    e => e,
  );

  type _TRelation_AR_FNP = {
    accountsReceivable: FAM.IAccountsReceivable,
    feesAndPremium: FAM.IFeesAndPremiums,
    plan: FAM.IPlan
  };

  let feesAndPremiums_to_accountsReceivable: Record<string, _TRelation_AR_FNP[]> = {};
  let accountsReceivable_to_feesAndPremiums: Record<string, _TRelation_AR_FNP[]> = {};

  stateGroupNumbers.forEach(eachGroupNumber => {
    // group number is deactivated, skip
    if (!eachGroupNumber || eachGroupNumber.deactivated)
      return;

    const plan = eachGroupNumber?.planRef?.id ? plansLookup[eachGroupNumber.planRef.id] : null;
    const feesAndPremium = plan?.feesAndPremiumsRef?.id ? feesAndPremiumsLookup[plan.feesAndPremiumsRef.id] : null;
    const accountsReceivable = eachGroupNumber?.accountsReceivableRef?.id ? accountsReceivableLookup[eachGroupNumber.accountsReceivableRef.id] : null;

    // any element of relationship not found, skip
    if (!plan || !feesAndPremium || !accountsReceivable)
      return;

    // any element of relationship is deactivated, skip
    if (plan.deactivated || feesAndPremium.deactivated || accountsReceivable.deactivated)
      return;

    const relation: _TRelation_AR_FNP = {
      plan: plan,
      feesAndPremium: feesAndPremium,
      accountsReceivable: accountsReceivable,
    }

    // Fees and Premiums to Accounts Receivable
    // -----------------------------------------
    {
      const relations = feesAndPremiums_to_accountsReceivable[feesAndPremium.id] ?? [];
      feesAndPremiums_to_accountsReceivable[feesAndPremium.id] = relations;

      // only add if it is not already in the list
      if (relations.length === 0)
        relations.push(relation);
      else if (!relations.some(t => t.accountsReceivable.id === accountsReceivable.id))
        relations.push(relation);
    }

    // Accounts Receivable to Fees and Premiums
    // -----------------------------------------
    {
      const relations = accountsReceivable_to_feesAndPremiums[accountsReceivable.id] ?? [];
      accountsReceivable_to_feesAndPremiums[accountsReceivable.id] = relations;

      // only add if it is not already in the list
      if (relations.length === 0)
        relations.push(relation);
      else if (!relations.some(t => t.feesAndPremium.id === feesAndPremium.id))
        relations.push(relation);
    }

  });

  // Clean up relations that we dont need to process
  if (theFNP) {
    const relatedARs = feesAndPremiums_to_accountsReceivable[theFNP.id] ?? [];
    const accountsReceivable_to_feesAndPremiums_subset: typeof accountsReceivable_to_feesAndPremiums = {};
    relatedARs.forEach(ar => {
      accountsReceivable_to_feesAndPremiums_subset[ar.accountsReceivable.id] = accountsReceivable_to_feesAndPremiums[ar.accountsReceivable.id];
    });

    accountsReceivable_to_feesAndPremiums = accountsReceivable_to_feesAndPremiums_subset;
  }

  stateClaimsARs?.forEach(accountsReceivable => {

    if (accountsReceivable.deactivated)
      return;

    // All Shared Savings Cap Amounts, Display CE Line Item, IPRP - CE Bill Group List
    const bcbs_sharedsavings_capamount_list: number[] = [];
    const bcbs_iprpcebillgroup_fp_list: boolean[] = [];
    const bcbs_displayceline_fp_list: boolean[] = [];

    const relatedFNPsToARs = accountsReceivable_to_feesAndPremiums[accountsReceivable.id];

    // any valid fees and premiums for this accounts receivable?
    if (!relatedFNPsToARs || relatedFNPsToARs.length === 0)
      return;


    // Extract
    // -----------------------------------------
    // these are different fees and premiums that are associated with the this accounts receivable
    relatedFNPsToARs.forEach(({ feesAndPremium, plan }) => {

      // invalid relation?
      if (feesAndPremium.deactivated || plan.deactivated)
        return;

      // lets extract data from attributeset
      feesAndPremium.attributeSets.forEach(eachAttributeSet => {

        if (eachAttributeSet.deactivated)
          return;

        // Shared Savings Attribute Set ?
        if (eachAttributeSet.attributeSetRef.id === "296bac98-575e-ee11-be6f-000d3a3bec27") {

          const fields = FieldsToLookupByMasterAttributeId(config, eachAttributeSet.fields);

          // Shared Savings Category
          // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=2b6bac98-575e-ee11-be6f-000d3a3bec27
          // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=7b606da3-585e-ee11-be6f-000d3a3bec27
          const bcbs_sharedsavings_categorycode = fields['2b6bac98-575e-ee11-be6f-000d3a3bec27'];
          const bcbs_sharedsavings_categorycode_value = bcbs_sharedsavings_categorycode?.[0].field?.values?.[0]?.value?.type === 'entity'
            ? bcbs_sharedsavings_categorycode[0].field.values[0].value
            : null;

          // Claims Edits ?
          // "entity/bcbs_config_optionsetitem/Claims Edits/af7e9d03-565e-ee11-be71-000d3a3be219"
          // shared saving claim type is not set to claims edit? skip
          if (bcbs_sharedsavings_categorycode_value?.id !== 'af7e9d03-565e-ee11-be71-000d3a3be219')
            return;

          // Cap Amount
          // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=356bac98-575e-ee11-be6f-000d3a3bec27
          // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=8b606da3-585e-ee11-be6f-000d3a3bec27
          const bcbs_sharedsavings_capamount = fields['356bac98-575e-ee11-be6f-000d3a3bec27'];
          const bcbs_sharedsavings_capamount_value = bcbs_sharedsavings_capamount?.[0].field?.values?.[0]?.value?.type === 'number'
            ? bcbs_sharedsavings_capamount[0].field.values[0].value
            : null;

          if (bcbs_sharedsavings_capamount_value)
            bcbs_sharedsavings_capamount_list.push(bcbs_sharedsavings_capamount_value.value);

          // IPRP - CE Bill Group
          // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=df4545c8-eb49-ef11-a317-6045bda7a170
          // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=fe3e8c62-ec49-ef11-a317-6045bda7a170
          const bcbs_iprpcebillgroup_fp = fields['df4545c8-eb49-ef11-a317-6045bda7a170'];
          const bcbs_iprpcebillgroup_fp_value = bcbs_iprpcebillgroup_fp?.[0].field?.values?.[0]?.value?.type === 'bool'
            ? bcbs_iprpcebillgroup_fp[0].field.values[0].value
            : null;

          if (bcbs_iprpcebillgroup_fp_value)
            bcbs_iprpcebillgroup_fp_list.push(bcbs_iprpcebillgroup_fp_value.value);

          // Display SS CE as Line Item
          // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=f2cfe8e9-ec49-ef11-a317-6045bda7a170
          // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=20bb3b3e-ed49-ef11-a317-6045bda7a170
          const bcbs_displayceline_fp = fields['f2cfe8e9-ec49-ef11-a317-6045bda7a170'];
          const bcbs_displayceline_fp_value = bcbs_displayceline_fp?.[0].field?.values?.[0]?.value?.type === 'bool'
            ? bcbs_displayceline_fp[0].field.values[0].value
            : null;

          if (bcbs_displayceline_fp_value)
            bcbs_displayceline_fp_list.push(bcbs_displayceline_fp_value.value);
        }
      });
    });

    // anything extracted?
    if (!(bcbs_sharedsavings_capamount_list.length > 0 || bcbs_iprpcebillgroup_fp_list.length > 0 || bcbs_displayceline_fp_list.length > 0))
      return;

    // Update
    // -----------------------------------------
    // caching accountsReceivable fields, since we will be updating AR cap amount, IPRP, Display SS CE as Line Item
    const fields = FieldsToLookupByMasterAttributeId(config, accountsReceivable.fields);

    // AR CE Cap Amount (master)
    // Claims Edit Cap Amount
    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=3e347f02-8d4a-ef11-a317-000d3a17460e
    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=5fde294b-8d4a-ef11-a317-000d3a17460e
    {
      const everyValueSame = bcbs_sharedsavings_capamount_list.every(q => q === bcbs_sharedsavings_capamount_list[0]);
      const bcbs_cecapamount_ar = fields['3e347f02-8d4a-ef11-a317-000d3a17460e'];

      if (bcbs_cecapamount_ar) {
        if (everyValueSame)
          bcbs_cecapamount_ar[0].field.values[0].value = {
            type: 'number',
            value: bcbs_sharedsavings_capamount_list[0],
          }
        else {
          retResult.errors.push(`The related Accounts Receivable (${accountsReceivable.name}) Cap Amount was reset, the Fees and Premium have different cap amounts (${bcbs_sharedsavings_capamount_list.join(", ")}).`);
          bcbs_cecapamount_ar[0].field.values[0].value = null;
        }

      }
    }

    // AR IPRP - CE Bill Group (master)
    // IPRP - Claims Edit Bill Group 
    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=03961c12-004a-ef11-a317-6045bda7a170
    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=81afaa01-8b4a-ef11-a317-000d3a17460e
    {
      const anyTrue = bcbs_iprpcebillgroup_fp_list.some(q => q === true);
      const bcbs_iprpcebillgroup_ar = fields['03961c12-004a-ef11-a317-6045bda7a170'];
      if (bcbs_iprpcebillgroup_ar)
        bcbs_iprpcebillgroup_ar[0].field.values[0].value = {
          type: 'bool',
          value: anyTrue,
        }
    }

    // AR Display SS CE as Line Item (master)
    // Display IPRP Claims Edit on Bill
    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_masterattribute&id=19f22ace-8b4a-ef11-a317-000d3a17460e
    // https://bcbsonlinesys.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=bc262b0e-8c4a-ef11-a317-000d3a17460e
    {
      const anyTrue = bcbs_displayceline_fp_list.some(q => q === true);
      const bcbs_displayssceline_ar = fields['19f22ace-8b4a-ef11-a317-000d3a17460e'];

      if (bcbs_displayssceline_ar)
        bcbs_displayssceline_ar[0].field.values[0].value = {
          type: 'bool',
          value: anyTrue,
        }
    }

  });

  return retResult;

}